import type { TableAction } from "@shared/types/data-table"
import type { Customer } from "@shared/types/customers"
import { createBaseActions, mergeActions, type BaseActionHandlers } from "./base-actions"
import { Ban } from "lucide-react"

interface CustomerSpecificHandlers {
  onBlock?: (customer: Customer) => void
  onBulkBlock?: (customerIds: string[]) => void
  onViewCustomer?: (customer: Customer) => void
  onCreatePayment?: (customer: Customer) => void
  onCreateSubscription?: (customer: Customer) => void
  onCreateInvoice?: (customer: Customer) => void
  onViewProfile?: (customer: Customer) => void
  onFlag?: (customer: Customer) => void
  onAddNote?: (customer: Customer) => void
  onCopyCustomerId?: (customer: Customer) => void
  onBlacklist?: (customer: Customer) => void
  onWhitelist?: (customer: Customer) => void
}

export type CustomerActionHandlers = BaseActionHandlers<Customer> & CustomerSpecificHandlers

// Row actions for tables - quick access operations
const createCustomerRowActions = (handlers: CustomerActionHandlers) => [
  {
    key: "copyId",
    label: "Copy customer ID",
    onClick: (customer: Customer) => handlers.onCopyCustomerId?.(customer),
  },
  {
    key: "viewProfile",
    label: "View customer profile",
    onClick: (customer: Customer) => handlers.onViewProfile?.(customer),
  },
  {
    key: "createPayment",
    label: "Create payment",
    onClick: (customer: Customer) => handlers.onCreatePayment?.(customer),
    condition: (customer: Customer) => customer.status === "active",
  },
  {
    key: "flag",
    label: "Flag for review",
    onClick: (customer: Customer) => handlers.onFlag?.(customer),
  },
  {
    key: "addNote",
    label: "Add note",
    onClick: (customer: Customer) => handlers.onAddNote?.(customer),
  },
  {
    key: "whitelist",
    label: "Add to whitelist",
    onClick: (customer: Customer) => handlers.onWhitelist?.(customer),
  },
  {
    key: "blacklist",
    label: "Add to blacklist",
    onClick: (customer: Customer) => handlers.onBlacklist?.(customer),
    variant: "destructive" as const,
  },
  {
    key: "block",
    label: "Block customer",
    onClick: (customer: Customer) => handlers.onBlock?.(customer),
    variant: "destructive" as const,
    condition: (customer: Customer) => customer.status !== "terminated",
  },
]

// Panel actions for detail pages - comprehensive operations
const createCustomerPanelActions = (handlers: CustomerActionHandlers) => [
  {
    key: "createPayment",
    label: "Create payment",
    onClick: (customer: Customer) => handlers.onCreatePayment?.(customer),
    condition: (customer: Customer) => customer.status === "active",
  },
  {
    key: "createSubscription",
    label: "Create subscription",
    onClick: (customer: Customer) => handlers.onCreateSubscription?.(customer),
    condition: (customer: Customer) => customer.status === "active",
  },
  {
    key: "createInvoice",
    label: "Create invoice",
    onClick: (customer: Customer) => handlers.onCreateInvoice?.(customer),
    condition: (customer: Customer) => customer.status === "active",
  },
  {
    key: "flag",
    label: "Flag",
    onClick: (customer: Customer) => handlers.onFlag?.(customer),
    variant: "outline" as const,
  },
  {
    key: "addNote",
    label: "Add note",
    onClick: (customer: Customer) => handlers.onAddNote?.(customer),
  },
  {
    key: "copyId",
    label: "Copy customer ID",
    onClick: (customer: Customer) => handlers.onCopyCustomerId?.(customer),
  },
  {
    key: "viewProfile",
    label: "View customer profile",
    onClick: (customer: Customer) => handlers.onViewProfile?.(customer),
  },
  {
    key: "block",
    label: "Block customer",
    onClick: (customer: Customer) => handlers.onBlock?.(customer),
    variant: "destructive" as const,
    condition: (customer: Customer) => customer.status !== "terminated",
  },
]

export const getCustomerActions = (handlers: CustomerActionHandlers = {}) => {
  const baseActions = createBaseActions('Customer', handlers, {
    showEdit: true,
    showDelete: false,
    showFlag: false,
    showAddNote: false,
    showCopyId: true,
    showExport: true,
  })

  return mergeActions(baseActions, {
    bulkActions: [
      {
        key: "block",
        label: "Block Customers",
        icon: <Ban className="h-4 w-4" />,
        onClick: (ids: string[]) => handlers.onBulkBlock?.(ids),
        variant: "destructive" as const,
        disabled: (ids: string[]) => ids.length === 0,
      },
    ],
    rowActions: createCustomerRowActions(handlers),
  })
}

export const getCustomerBulkActions = (handlers: CustomerActionHandlers = {}) =>
  getCustomerActions(handlers).bulkActions

export const getCustomerRowActions = (handlers: CustomerActionHandlers = {}) =>
  getCustomerActions(handlers).rowActions

export const getCustomerPanelActions = (handlers: CustomerActionHandlers = {}) =>
  createCustomerPanelActions(handlers)

export const getCustomerHoverActions = (handlers: CustomerActionHandlers = {}): TableAction<Customer>[] => [
  {
    key: "edit",
    label: "Edit customer",
    onClick: (customer) => handlers.onEdit?.(customer),
  },
]
